import { PRIVATE_MIRROR } from '../../lib/feedContext';
import { useState } from 'react';

import { Who } from '../../access/Who';
import { useSession } from '../../access/session';
import { MIRROR_FEED } from '../../lib/build';
import { useDeskCall } from '../deskCall';
import { isPilot } from '../dry';
import { useFilters } from '../filters';
import { useFeed3Nav } from '../feed3Standby';
import { booksInAir } from '../mirrorDesk';
import { parked } from '../parked';
import {
  learnHref,
  dryHref,
  eventsHref,
  experimentsHref,
  feed3Href,
  isExperiments,
  liveHref,
  pageLine,
  prematchHref,
  reportHref,
  studioHref,
} from '../route';
import { type Mode, type Status, useScreen } from '../store';
import { rememberLinePick } from './prematch';
import { setWebOnly, webOnly } from './detect';
import { usePhoneQueue } from './queue';

const STATUS_TEXT: Record<Status, string> = {
  connecting: 'соединение',
  live: 'в эфире',
  reconnecting: 'связь потеряна',
  paused: 'обновляемся, сейчас вернёмся',
  revoked: 'лента на другом экране',
};

export function PhoneHead({
  where = 'app',
}: {
  modes: readonly Mode[];
  where?: 'app' | 'mid' | 'transfers' | 'ru' | 'predict' | 'report' | 'dry' | 'studio' | 'events' | 'learn' | 'cash' | 'forecast';
  beat?: { text: string; mood: 'ok' | 'still' | 'warn' };
}): React.ReactElement {
  const [more, setMore] = useState(false);
  const status = useScreen((s) => s.status);
  const meta = useScreen((s) => s.meta);
  const setMode = useScreen((s) => s.setMode);
  const setShelf = useScreen((s) => s.setShelf);
  const forks = useScreen((s) => s.forks);
  const lines = useFilters((s) => s.lines);
  const journal = Boolean(useSession((s) => s.me.id)) && !PRIVATE_MIRROR;
  const pilot = isPilot({
    id: useSession((s) => s.me.id),
    username: useSession((s) => s.me.username),
  });
  const feed3on = useFeed3Nav() && !PRIVATE_MIRROR;
  const openHand = useDeskCall((s) => s.openHand);
  const onlyWeb = usePhoneQueue((s) => s.webOnly);
  const bookN = booksInAir(forks.values(), meta).length;
  const liveN = MIRROR_FEED ? bookN : meta.sources.filter((source) => !source.stale).length;
  const health = {
    text: `${liveN} в линии`,
    mood: liveN === 0 ? ('silent' as const) : ('ok' as const),
  };
  const tests = isExperiments(location.pathname);
  const now = pageLine(location.pathname);
  const testLine = lines.length === 1 ? lines[0] : null;
  const pip = status === 'live' ? health.text : STATUS_TEXT[status];
  const liveOn = tests ? testLine === 'live' : now === 'live';
  const preOn = tests ? testLine === 'prematch' : now === 'prematch';

  const goAppLine = (which: 'live' | 'prematch'): void => {
    rememberLinePick();
    setMode('feed');
    setShelf('work');
    location.assign(which === 'live' ? liveHref() : prematchHref());
  };

  const goTestLine = (which: 'live' | 'prematch'): void => {
    useFilters.getState().apply({ lines: [which] });
  };

  const goLine = tests ? goTestLine : goAppLine;

  return (
    <header className="ph-head">
      <div className="ph-head__row">
        <span className="ph-head__mark">FORQ{tests ? ' 2.0' : ''}</span>
        {where !== 'report' && where !== 'dry' && (
        <nav className="ph-head__line" aria-label="линия">
          <button
            type="button"
            className={liveOn ? 'on' : ''}
            aria-current={liveOn ? 'page' : undefined}
            onClick={() => goLine('live')}
          >
            лайв
          </button>
          <button
            type="button"
            className={preOn ? 'on' : ''}
            aria-current={preOn ? 'page' : undefined}
            onClick={() => goLine('prematch')}
          >
            прематч
          </button>
        </nav>
        )}
        <button type="button" className="ph-head__more" aria-expanded={more} aria-label="меню" onClick={() => setMore((v) => !v)}>
          ⋮
        </button>
      </div>
      <p className="ph-head__pip" data-mood={status !== 'live' || health.mood === 'silent' ? 'warn' : health.mood}>
        {pip}
      </p>
      {liveOn && (
        <p className="ph-head__hint">лайв: цена уходит, пока открываете контору</p>
      )}
      {status === 'revoked' && (
        <p className="ph-seat" role="status">
          лента открыта на другом экране. одно место — одно устройство. владелец может выдать второе.
        </p>
      )}
      {more && (
        <div className="ph-menu" role="menu">
          {!PRIVATE_MIRROR && <a href={learnHref()} role="menuitem" aria-current={where === 'learn' ? 'page' : undefined}>обучение</a>}
          <a href={studioHref()} role="menuitem">студия</a>
          {!PRIVATE_MIRROR && <a href={eventsHref()} role="menuitem" aria-current={where === 'events' ? 'page' : undefined}>события</a>}
          {tests ? (
            <a href="/app/live" role="menuitem">лента /app</a>
          ) : (
            !PRIVATE_MIRROR && !parked.lenta2 && <a href={`${experimentsHref()}/live`} role="menuitem">лента 2.0</a>
          )}
          {feed3on && <a href={feed3Href()} role="menuitem">лента 3.0</a>}
          {pilot && !PRIVATE_MIRROR && <a href={dryHref()} role="menuitem">сухой</a>}
          {journal && <a href={reportHref()} role="menuitem">моя статистика</a>}
          {!PRIVATE_MIRROR && <button type="button" role="menuitem" onClick={() => openHand()}>
            call
          </button>}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const next = !onlyWeb;
              setWebOnly(next);
              usePhoneQueue.getState().setWebOnly(next);
            }}
          >
            {onlyWeb || webOnly() ? 'сайт, не приложение · вкл' : 'сайт, не приложение'}
          </button>
          <Who />
        </div>
      )}
    </header>
  );
}
